import { useQueryClient } from '@tanstack/react-query'
import { CatchBoundary, Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { LogOutIcon, PlusIcon, SearchIcon } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { GithubDoneDialog } from '@/components/github-done-dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Kbd, KbdGroup } from '@/components/ui/kbd'
import { initials } from '@/lib/format'
import { hasUndoable, queryKeys, useCounts, useRunTool, useSettings } from '@/lib/items'
import { isMac, isTypingTarget, overlayOpen } from '@/lib/keys'
import { toSendLabel, userToday } from '@/lib/plan'
import { useSession, useUserNow } from '@/lib/session'
import { attention } from '@/lib/styles'
import { cn } from '@/lib/utils'
import { useVoiceAvailable } from '@/lib/voice/available'
import { AwayChip } from './away-chip'
import { CaptureDialog } from './capture-dialog'
import { CommandPalette } from './command-palette'
import { ErrorView } from './error-view'
import { ItemSheet } from './item-sheet'
import { LiveIndicator } from './live-indicator'
import { accountNav, allNav, type NavItem, topNav } from './nav'
import { useWorkspace, WorkspaceProvider } from './workspace'

/**
 * Global shortcuts: "g" then a letter jumps to a page (g i inbox, g t tasks, …); z (or ⌘Z)
 * undoes the last change; v dictates a new item. Runs in the capture phase so a consumed key
 * never reaches list views.
 */
function useGlobalShortcuts() {
  const navigate = useNavigate()
  const armedUntil = useRef(0)
  const { undoLast } = useRunTool()
  const { openDictation } = useWorkspace()
  const voice = useVoiceAvailable()
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || isTypingTarget(event.target)) return
      // z or ⌘Z / Ctrl+Z (outside text fields) undoes the last change.
      const undoKey = event.key.toLowerCase() === 'z' && !event.shiftKey
      if (undoKey && !overlayOpen() && Date.now() >= armedUntil.current) {
        if (!hasUndoable()) return
        event.preventDefault()
        void undoLast()
        return
      }
      if (event.metaKey || event.ctrlKey) return
      // v dictates a new item (when this server and browser can).
      if (event.key === 'v' && voice && !overlayOpen() && Date.now() >= armedUntil.current) {
        event.preventDefault()
        openDictation()
        return
      }
      if (event.key === 'g' && !overlayOpen()) {
        armedUntil.current = Date.now() + 1200
        event.preventDefault()
        return
      }
      if (Date.now() < armedUntil.current) {
        const item = allNav.find((n) => n.key === event.key.toLowerCase())
        armedUntil.current = 0
        event.preventDefault()
        if (item) void navigate({ to: item.to })
      }
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [navigate, undoLast, openDictation, voice])
}

/**
 * When the user's day changes (midnight in their time zone), what depends on today is fetched
 * again: plans, heads-ups, reminders and the counts. Nothing on the server pushes this. A
 * component of its own, so its minute tick re-renders nothing else.
 */
function NewDayRefresh() {
  const queryClient = useQueryClient()
  const settings = useSettings()
  const now = useUserNow(60_000)
  const { today } = userToday(now, settings.data?.timezone ?? 'Europe/Zurich')
  const seen = useRef(today)
  useEffect(() => {
    if (seen.current === today) return
    seen.current = today
    void queryClient.invalidateQueries({ queryKey: queryKeys.items })
    void queryClient.invalidateQueries({ queryKey: queryKeys.headsUps })
  }, [today, queryClient])
  return null
}

/** What the badge on a top-bar link says, for screen readers ("3 to sort"). */
function badgeText(item: NavItem, count: number, dotLabel: string): string {
  const parts: string[] = []
  if (count > 0) parts.push(item.count === 'inbox' ? `${count} to sort` : `${count} to approve`)
  if (dotLabel) parts.push(dotLabel)
  return parts.join(', ')
}

/**
 * A page in the top bar: icon and name (the name only from wide screens on), its count (items to
 * sort, drafts to approve) and, as an amber dot, what needs attention there (heads-ups and
 * reminders to send). On phones the badges sit on the icon.
 */
function TopNavLink({ item }: { item: NavItem }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const counts = useCounts()
  const descriptionId = useId()
  const count = item.count && counts.data ? counts.data[item.count] : 0
  const dot = item.dot && counts.data ? counts.data.headsUps + counts.data.reminders : 0
  const dotLabel =
    dot > 0 && counts.data ? toSendLabel(counts.data.headsUps, counts.data.reminders) : ''
  const description = badgeText(item, count, dotLabel)
  const active = pathname === item.to || pathname.startsWith(`${item.to}/`)
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      aria-describedby={description ? descriptionId : undefined}
      title={`${item.label} (G ${item.key.toUpperCase()})`}
      data-active={active}
      className={cn(
        'relative inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2 text-sm font-medium text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50',
        active && 'bg-muted text-foreground',
      )}
    >
      <item.icon aria-hidden="true" className="size-4 shrink-0" />
      <span className="max-lg:sr-only">{item.label}</span>
      {count > 0 || dot > 0 ? (
        <span
          aria-hidden="true"
          className="flex items-center gap-0.5 max-lg:absolute max-lg:-top-1 max-lg:-right-1"
        >
          {dot > 0 ? (
            <span
              role="img"
              aria-label={dotLabel}
              title={dotLabel}
              data-testid="count-headsups"
              className={cn(
                'flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[0.625rem] leading-none font-semibold',
                attention.dot,
              )}
            >
              {dot}
            </span>
          ) : null}
          {count > 0 ? (
            <span
              data-testid={`count-${item.count}`}
              className="flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground px-1 text-[0.625rem] leading-none font-semibold text-background tabular-nums lg:bg-muted-foreground/15 lg:text-foreground"
            >
              {count}
            </span>
          ) : null}
        </span>
      ) : null}
      {description ? (
        // Hidden but referenced: the link is named "Inbox" and described as "3 to sort".
        <span id={descriptionId} hidden>
          {description}
        </span>
      ) : null}
    </Link>
  )
}

function UserMenu() {
  const { me, signOut } = useSession()
  const navigate = useNavigate()
  if (!me) return null
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            aria-label={`Account menu for ${me.user.name}`}
          />
        }
      >
        <Avatar className="size-7">
          {me.user.image ? <AvatarImage src={me.user.image} alt="" /> : null}
          <AvatarFallback>{initials(me.user.name)}</AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuGroup>
          <DropdownMenuLabel>
            <div className="truncate font-medium text-foreground">{me.user.name}</div>
            <div className="truncate text-xs font-normal" data-testid="user-email">
              {me.user.email}
            </div>
            {me.testMode ? (
              <Badge variant="outline" className="mt-1.5" data-testid="test-mode">
                Test mode
              </Badge>
            ) : null}
          </DropdownMenuLabel>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        {accountNav.map((item) => (
          <DropdownMenuItem key={item.to} onClick={() => void navigate({ to: item.to })}>
            <item.icon aria-hidden="true" />
            {item.label}
            <DropdownMenuShortcut className="max-md:hidden">
              G {item.key.toUpperCase()}
            </DropdownMenuShortcut>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => void signOut()}>
          <LogOutIcon aria-hidden="true" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function AppShell() {
  return (
    <WorkspaceProvider>
      <Shell />
    </WorkspaceProvider>
  )
}

function Shell() {
  const { live } = useSession()
  const { setCaptureOpen } = useWorkspace()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  useGlobalShortcuts()

  return (
    <div className="flex min-h-svh flex-col">
      <NewDayRefresh />
      <header className="sticky top-0 z-10 shrink-0 border-b bg-background/80 pt-(--safe-top) backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-14 min-w-0 items-center gap-1 px-2 sm:gap-2 sm:px-3 md:px-4">
          <Link
            to="/today"
            aria-label="hugo"
            className="mr-1 hidden shrink-0 items-center gap-2 rounded-md outline-none focus-visible:ring-3 focus-visible:ring-ring/50 md:flex"
          >
            <img src="/favicon.svg" alt="" className="size-6 rounded-md ring-1 ring-border" />
            <span className="font-heading text-lg font-semibold max-xl:hidden">hugo</span>
          </Link>
          <nav aria-label="Main navigation" className="flex shrink-0 items-center gap-0.5">
            {topNav.map((item) => (
              <TopNavLink key={item.to} item={item} />
            ))}
          </nav>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto min-w-0 shrink justify-start gap-2 text-muted-foreground max-md:size-8 max-md:justify-center max-md:px-0 md:ml-2 md:flex-1 md:max-w-64"
            onClick={() => setPaletteOpen(true)}
            aria-label="Open command palette"
          >
            <SearchIcon aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate text-left max-md:hidden">
              Search or run a command
            </span>
            <KbdGroup className="hidden lg:inline-flex">
              <Kbd>{isMac ? '⌘' : 'Ctrl'}</Kbd>
              <Kbd>K</Kbd>
            </KbdGroup>
          </Button>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2 md:ml-auto">
            <AwayChip />
            {/* One capture button: the dialog has the microphone (V dictates right away). */}
            <Button
              variant="default"
              size="icon-sm"
              aria-label="New item (C)"
              title="New item (C)"
              onClick={() => setCaptureOpen(true)}
            >
              <PlusIcon aria-hidden="true" />
            </Button>
            <LiveIndicator status={live} />
            <UserMenu />
          </div>
        </div>
      </header>
      <div className="flex min-w-0 flex-1 flex-col pb-(--safe-bottom)">
        <CatchBoundary getResetKey={() => pathname} errorComponent={ErrorView}>
          <Outlet />
        </CatchBoundary>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <CaptureDialog />
      <GithubDoneDialog />
      <ItemSheet />
    </div>
  )
}
